/**
 * API de Inventario (inventory.php)
 * Endpoints: items, reports, adjust
 */

import { dotoniwoofApi } from '../api/dotoniwooApi';

const BASE = '/inventory.php';

/**
 * GET /items - Lista paginada de items con stock
 * @param {Object} params - { page, limit, id_product, sku, stock_min, stock_max, id_weight? }
 */
export const getInventoryItems = async (params = {}) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/items`, { params });
    const data = response.data;
    const items = data?.data ?? data?.message ?? [];
    return {
      ok: true,
      status: response.status,
      code: data?.code,
      total_rows: data?.total_rows ?? (Array.isArray(items) ? items.length : 0),
      total_pages: data?.total_pages ?? 1,
      page: data?.page ?? 1,
      data: Array.isArray(items) ? items : []
    };
  } catch (error) {
    console.error('Error al obtener items de inventario:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /items/{id_item} - Detalle de item con últimos movimientos
 */
export const getInventoryItemById = async (idItem) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/items/${idItem}`);
    const data = response.data;
    const itemData = data?.data ?? data?.message ?? data;
    return {
      ok: true,
      status: response.status,
      data: itemData
    };
  } catch (error) {
    console.error('Error al obtener item:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/movements - Movimientos por item y rango de fechas
 * @param {Object} params - { id_item, date_from, date_to, page, limit, movement_type?, doc_type? }
 * movement_type: 'in' = solo entradas, 'out' = solo salidas, omitir = todos
 * doc_type: filtrar por tipo de documento (APERTURA, VENTA, AJUSTE, COMPRA, etc.)
 */
export const getMovementsReport = async (params = {}) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/movements`, { params });
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      total_rows: data?.total_rows ?? 0,
      total_pages: data?.total_pages ?? 1,
      page: data?.page ?? 1,
      data: data?.data ?? []
    };
  } catch (error) {
    console.error('Error al obtener movimientos:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/stock-summary - Resumen de stock por producto o categoría
 * @param {Object} params - { id_product, id_category }
 */
export const getStockSummaryReport = async (params = {}) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/stock-summary`, { params });
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      data: data?.data ?? data
    };
  } catch (error) {
    console.error('Error al obtener resumen de stock:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/low-stock - Items con stock bajo
 * @param {Object} params - { threshold }
 */
export const getLowStockReport = async (threshold = 5) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/low-stock`, {
      params: { threshold }
    });
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      threshold: data?.threshold ?? threshold,
      data: data?.data ?? []
    };
  } catch (error) {
    console.error('Error al obtener stock bajo:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/valuation - Valoración total del inventario
 */
export const getValuationReport = async () => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/valuation`);
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      data: data?.data ?? data
    };
  } catch (error) {
    console.error('Error al obtener valoración:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/in-out-summary - Entradas y salidas por período
 * @param {Object} params - { date_from, date_to }
 */
export const getInOutSummaryReport = async (dateFrom, dateTo) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/in-out-summary`, {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      date_from: data?.date_from,
      date_to: data?.date_to,
      data: data?.data ?? []
    };
  } catch (error) {
    console.error('Error al obtener resumen in/out:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * GET /reports/by-reference - Historial por documento de referencia
 * @param {Object} params - { ref_table, ref_id }
 */
export const getByReferenceReport = async (refTable, refId) => {
  try {
    const response = await dotoniwoofApi.get(`${BASE}/reports/by-reference`, {
      params: { ref_table: refTable, ref_id: refId }
    });
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      data: data?.data ?? data
    };
  } catch (error) {
    console.error('Error al obtener por referencia:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * POST /adjust - Ajuste manual de inventario
 * @param {Object} payload - { firebase_UID, id_item, quantity, adjustment_type, unit_cost?, notes? }
 */
export const postInventoryAdjust = async (payload) => {
  try {
    const response = await dotoniwoofApi.post(`${BASE}/adjust`, payload);
    const data = response.data;
    return {
      ok: true,
      status: response.status,
      code: data?.code,
      data: data
    };
  } catch (error) {
    console.error('Error al ajustar inventario:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error al ajustar inventario' }
    };
  }
};
